import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
// Phase 23-08: useListMembers removed (members table dropped). Use useListAccounts instead.
import {
  useListAccounts,
  useListInventoryPlans,
  getListAccountsQueryKey,
} from "@workspace/api-client-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetTrigger } from "@/components/ui/sheet";
import { UserPlus, X } from "lucide-react";
import { MembersTable } from "@/components/members/members-table";
import { ListPagination } from "@/components/common/list-pagination";
import { WalkInWizard } from "@/components/members/walk-in-wizard";
import { PlanCoverageAlert } from "@/components/billing/plan-coverage-alert";

// Phase 10 — chips are now per planName. operator-friendly labels map planName → display.
const PLAN_DISPLAY_LABEL: Record<string, string> = {
  Workstation: "Workstation",
  Private_Suite: "Private Suite",
  Day_Pass: "Day Pass",
  Five_Days_Pass: "5 Days Pass",
  Week_Pass: "Week Pass",
  Meeting_Room: "Meeting Room",
  Conference_Room: "Conference Room",
  Event: "Event",
  Shooting: "Shooting",
};

function planDisplayLabel(planName: string): string {
  return PLAN_DISPLAY_LABEL[planName] ?? planName.replace(/_/g, " ");
}

export default function Members() {
  const [, navigate] = useLocation();
  const [tab, setTab] = useState<"all" | "recurring" | "non-recurring">("all");
  const [chips, setChips] = useState<Set<number>>(new Set());  // Phase 10 — chip values are now planId
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [walkInOpen, setWalkInOpen] = useState(false);

  const { data: plans } = useListInventoryPlans();

  // Phase 10 (Plan 10-07): server-side memberCategory filter removed — cadence derives via plan.cadence client-side.
  const cadenceFilter =
    tab === "recurring" ? "Recurring" as const :
    tab === "non-recurring" ? "Non_Recurring" as const :
    undefined;

  const params = {
    status: "Active" as const,
    ...(search ? { search } : {}),
  };

  const { data: members, isLoading } = useListAccounts({ status: "active" as const }, {
    query: { queryKey: getListAccountsQueryKey({ status: "active" as const }) },
  });

  // Phase 23-08: accounts don't carry planId directly.
  // cadence filter: map via account.kind (member → Recurring, walk_in → Non_Recurring)
  const planMap = useMemo(() => {
    const m = new Map<number, { planName: string; cadence: "Recurring" | "Non_Recurring" }>();
    (plans ?? []).forEach((p) => m.set(p.id, { planName: p.planName, cadence: p.cadence }));
    return m;
  }, [plans]);

  void planMap; // reserved for plan-based filtering in future phases

  const filtered = (members ?? []).filter((m) => {
    if (cadenceFilter === "Recurring" && m.kind !== "member") return false;
    if (cadenceFilter === "Non_Recurring" && m.kind !== "walk_in") return false;
    return true; // chip filter not available without planId on account
  });
  // Client-side pagination over the filtered members (endpoint stays a bare array
  // so the member combobox keeps the full list). Status/search are server-side.
  const pagedMembers = filtered.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => {
    setPage(1);
  }, [tab, search, chips, pageSize]);

  // Plans visible as chips in current tab — filtered by tab cadence (or all if "all").
  const visiblePlans = (plans ?? []).filter((p) => {
    if (p.status !== "Active") return false;
    if (tab === "recurring") return p.cadence === "Recurring";
    if (tab === "non-recurring") return p.cadence === "Non_Recurring";
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Members</h1>
        <Sheet open={walkInOpen} onOpenChange={setWalkInOpen}>
          <SheetTrigger asChild>
            <Button>
              <UserPlus className="mr-2 h-4 w-4" />
              Walk-in
            </Button>
          </SheetTrigger>
          <WalkInWizard onClose={() => setWalkInOpen(false)} />
        </Sheet>
      </div>

      {/* VAL-05 surfacing — PlanCoverageAlert reused */}
      <PlanCoverageAlert />

      {/* Sub-tabs */}
      <Tabs value={tab} onValueChange={(v) => { setTab(v as typeof tab); setChips(new Set()); }}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="recurring">Recurring</TabsTrigger>
          <TabsTrigger value="non-recurring">Non-recurring</TabsTrigger>
        </TabsList>

        {/* Filter chips + search */}
        <div className="flex items-center gap-2 my-4 flex-wrap">
          {visiblePlans.map((p) => {
            const active = chips.has(p.id);
            return (
              <Button
                key={p.id}
                size="sm"
                variant={active ? "secondary" : "outline"}
                onClick={() => {
                  const next = new Set(chips);
                  if (active) next.delete(p.id); else next.add(p.id);
                  setChips(next);
                }}
              >
                {planDisplayLabel(p.planName)}
              </Button>
            );
          })}
          {chips.size > 0 && (
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setChips(new Set())}
              aria-label="Clear filters"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          <div className="ml-auto">
            <Input
              placeholder="Search members…"
              className="w-[200px]"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <TabsContent value="all">
          <MembersTable
            members={pagedMembers}
            variant="all"
            isLoading={isLoading}
            hasActiveFilter={chips.size > 0}
            onViewMember={(id) => navigate(`/members-legacy/${id}`)}
          />
        </TabsContent>
        <TabsContent value="recurring">
          <MembersTable
            members={pagedMembers}
            variant="recurring"
            isLoading={isLoading}
            hasActiveFilter={chips.size > 0}
            onViewMember={(id) => navigate(`/members-legacy/${id}`)}
          />
        </TabsContent>
        <TabsContent value="non-recurring">
          <MembersTable
            members={pagedMembers}
            variant="non-recurring"
            isLoading={isLoading}
            hasActiveFilter={chips.size > 0}
            onViewMember={(id) => navigate(`/members-legacy/${id}`)}
          />
        </TabsContent>
      </Tabs>
      {!isLoading && filtered.length > 0 ? (
        <ListPagination
          total={filtered.length}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          isLoading={isLoading}
          noun={["member", "members"]}
        />
      ) : null}
      {/* planMap is reserved for future enhancements (e.g., showing plan.planName in MembersTable);
          building it here keeps the planId → plan lookup hot for child components without a
          duplicate fetch. */}
      {import.meta.env.DEV && planMap.size === 0 && plans && (
        <div hidden>plans loaded but empty</div>
      )}
    </div>
  );
}
